import { IAbstractClass } from 'library/ts/interfaces/abstract/IAbstractClass';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { IAbstractTagPluginArgument } from 'plugins/tag/abstract/IAbstractTagPluginArgument';
import { IAbstractTagPlugin } from 'plugins/tag/abstract/interfaces/IAbstractTagPlugin';
import { ITagConfig } from 'tag/ts/tag/interfaces/config/ITagConfig';
import { ITagMVC } from 'tag/ts/tag/interfaces/mvc/ITagMVC';

/**
 * @description Abstract Tag Plugin class. Should be inherited by tag class type plugins
 * WARNING: Should be inherited by tag plugin application class ONLY
 */
export abstract class AbstractTagPlugin implements IAbstractTagPlugin {

	readonly #name: string;
	readonly #tagMVC: ITagMVC;
	readonly #owner: IAbstractClass | any;
	readonly #logger: ILogger;

	protected constructor(
		argument: IAbstractTagPluginArgument = {
			name: 'AbstractTagPlugin',
			framework: undefined,
			owner: undefined,
			config: undefined
		}
	) {
		this.#tagMVC = argument?.framework;
		this.#name = argument?.name;
		this.#owner = argument?.owner;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag', 'TagPlugin', `${this.#name}:` ] } );
	}

	/**
	 * @description Returns name of this class
	 * @return {string}
	 */
	get name(): string {
		return this.#name;
	}

	/**
	 * @desc Tag MVC structure
	 * @returns {ITagMVC}
	 */
	get tagMVC() {
		return this.#tagMVC;
	}

	/**
	 * @description Returns uuid of this Tag application shared by Tag, Skin, Plugins
	 * @return {string}
	 */
	get uuid(): string {
		return this?.tag?.uuid;
	}

	/**
	 * @description Returns instance of SmartTag wide Utilities
	 * @returns {Utilities | undefined}
	 */
	get utils() {
		return window?.SmartTag?.utilities;
	}

	/**
	 * @description SmartTag wide runtimeVO instance
	 * @returns {ISmartTagRuntimeVO | undefined}
	 */
	get runtimeVO() {
		return window?.SmartTag?.runtimeVO;
	}

	/**
	 * @description Tag application instance
	 * @returns {ITag | undefined}
	 */
	get tag() {
		return this?.tagMVC?.view?.tag;
	}

	/**
	 * @description Tag parameters
	 * @return {IParamsVO}
	 */
	get params() {
		return this?.tag?.params;
	}

	/**
	 * @description Tag configuration module
	 */
	get tagConfig(): ITagConfig {
		return this?.tag?.model?.config?.tag;
	}

	/**
	 * @description Plugin wide Logger instance
	 * @returns {ILogger}
	 */
	get logger(): ILogger {
		return this.#logger;
	}

	/**
	 * @description Adds event listeners for all events dispatched within this Skin instance.
	 * Redirects events to a Callback function passed from a Concrete class as a parameter
	 * @return {(callback: Function, events: TagEvent[]) => void}
	 */
	get tagEventsHandler() {
		return this?.tagMVC?.view?.tagEventsHandler;
	}

}